import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Callout, Card, Flex, Spinner, Text } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { routes } from '../../routes';
import { createClass } from '../../services/school/class/createClass';
import { ClassType } from '../../types/Class';
import { Key_courses, Key_departaments } from '../../utils/constants';
import { CreateClassFormValidation } from './CreateClassFormValidation';
import { useEffect } from 'react';
import { isEmpty, isNull } from 'lodash';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import CheckboxComponent from '../../components/inputs/Checkbox/CheckboxComponent';
import { stringToBoolean } from '../../utils/utilities';
import DateFieldComponent from '../../components/inputs/Datefield/DatefieldComponent';

export const CreateClass = () => {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: createClass, mutationKey: 'createClass' });
  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();
  const queryClient = useQueryClient();

  const defaultValues: ClassType = {
    description: '',
    idAcademicYear: currentAcademicYear?.id ?? "",
    idCourse: '',
    level: 0,
    semester: 0,
    idFaculty: '',
    idDepartament: '',
    regime: 1,
    useOwnRulesForAcademicPeriod: false
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, resetField } = useForm<ClassType>({
    resolver: yupResolver(CreateClassFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const idFaculty = watch('idFaculty');
  const idDepartament = watch('idDepartament');

  const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') });
  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery({
    PageNumber: 1, PageSize: 400,
    IdFaculty: idFaculty
  },
    {
      enabled: !!idFaculty
    });
  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery(
    { PageNumber: 1, PageSize: 400, IdDepartament: idDepartament },
    {
      enabled: !!idDepartament,
    }
  );

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        ['idCourse', 'level', 'semester', 'regime'].includes(name || '') &&
        value.idCourse &&
        value.level &&
        value.semester &&
        value.regime
      ) {
        generateClassName();
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, courses, currentAcademicYear]);

  if (!currentAcademicYear || !currentAcademicYear?.status) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Por favor active o ano lectivo
            <Link to={routes.academicYears} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }
  const handleFacultyChange = (idFaculty: string) => {
    if (!idFaculty) return;
    resetField('idDepartament')
    resetField('idCourse')
    setValue('idFaculty', idFaculty);
    // refreshCourses();
    queryClient.invalidateQueries(Key_departaments);
  };
  const handleDepartamentChange = (idDepartament: string) => {
    if (!idDepartament) return;
    resetField('idCourse');
    setValue('idDepartament', idDepartament);
    refreshCourses();

  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.idAcademicYear = currentAcademicYear.id;
      try {
        data.regime = Number(data.regime);
      } catch (ex) {
        console.error({ ex })
      }
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("Turma criado");
        navigate(routes.classes);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    navigate(routes.classes);
  }
  const handleUniversityChange = (id: string) => {
    setValue('idUniversity', id);
    setValue('idFaculty', '');
    setValue('idDepartament', '');
    refetchFaculties();
  };
  const isUseOwnRulesForAcademicPeriod = stringToBoolean(watch('useOwnRulesForAcademicPeriod'));

  const generateClassName = () => {
    const courseId = getValues('idCourse');
    const course = courses?.items.find(c => c.id === courseId);
    const level = getValues('level');
    const semester = getValues('semester');
    const regime = getValues('regime');
    const year = currentAcademicYear?.year || new Date().getFullYear();
    if (!course || !level || !semester || !regime || !year){
       setValue('description', '');
       return;
    } 
    const acronym = course.name
      ?.split(' ')
      .filter(word => word.length > 2 && /^[A-ZÁÉÍÓÚÂÊÔÇ]/i.test(word))
      .map(word => word[0])
      .join('')
      .toUpperCase() || 'TURMA';

    const regimeCode = {
      1: 'L', 
      2: 'P', 
      3: 'D'  
    }[Number(regime)] || 'X';
    const className = `${acronym}-${level}${regimeCode}-${year}-S${semester}`;
    setValue('description', className);
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>

          <Flex gap="8">
            <TextFieldComponent name="level" label="Ano" control={control} errors={errors} fieldProps={{ placeholder: 'Ano', type: 'number' }} style={{ width: '100%' }} required={true} />
            <TextFieldComponent name="semester" label="Semestre" control={control} errors={errors} fieldProps={{ placeholder: 'Semester', type: 'number' }} style={{ width: '100%' }} required={true} />

          </Flex>
          <SelectComponent
            name="idUniversity"
            label="Instituição de Ensino Superior"
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={universities?.items.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
            itemsLoading={isLoadingUniversities}
            itemsError={errorUniversities}
            onSelect={handleUniversityChange}
            required={true}
          />
          <SelectComponent name="idFaculty" label="Unidade orgânica" control={control} errors={errors} style={{ width: '100%' }}
            options={faculties?.items.map(faculty => ({
              value: faculty.id ?? "",
              label: faculty.description ?? ""
            }))}
            onSelect={handleFacultyChange}
            itemsLoading={isLoadingFaculties}
            itemsError={errorFaculties}
            required={true}
          />
          <SelectComponent name="idDepartament" label="Departamento" control={control} errors={errors} style={{ width: '100%' }}
            options={departaments?.items.map(departament => ({
              value: departament.id ?? "",
              label: departament.description ?? ""
            }))}
            onSelect={handleDepartamentChange}
            itemsLoading={isLoadingDepartaments}
            itemsError={errorDepartaments}
            required={true}
          />
          <SelectComponent name="idCourse" label="Curso" control={control} errors={errors} style={{ width: '100%' }}
            options={
              // idDepartament &&
              courses?.items?.map(course => ({
                value: course.id ?? "",
                label: course.name ?? ""
              })) || []}
            onSelect={(value: string) => {
              setValue('idCourse', value)
            }}
            itemsLoading={isLoadingCourses}
            itemsError={errorCourses}
            // fieldProps={{ disabled:(!idDepartament || isLoadingCourses)  }}
            required={true}
          />
          <Flex gap="8">
            <RadioGroupComponent name="regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "1", label: "Laboral" }
                , { value: "2", label: "Pós Laboral" }
                , { value: "3", label: "Distância" }
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: '16px' }
              }}
            />
          </Flex>
          <Flex gap="8">
            <TextFieldComponent name="description" label="Descrição" control={control} errors={errors} fieldProps={{ placeholder: 'Descrição da turma' }} style={{ width: '100%' }} required={true} />
          </Flex>
          <Flex gap='4'>
            <CheckboxComponent name="useOwnRulesForAcademicPeriod" label="Usa regras próprias para o período acadêmico ? " control={control} errors={errors} required={true} />
          </Flex>
          <Flex gap='4'>
            <DateFieldComponent name="start" label='Início' control={control} errors={errors} required={isUseOwnRulesForAcademicPeriod} style={{ width: '100%' }} />
            <DateFieldComponent name="end" label='Fim' control={control} errors={errors} required={isUseOwnRulesForAcademicPeriod} style={{ width: '100%' }} />
          </Flex>
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>


    </form>

  )
}
